import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import { trackViewItem, trackAddToCart } from "foundation/js/ecommerceTracking";

import AmountSelector from "components/AmountSelector/AmountSelector.vue";
import GetLabel from 'foundation/js/getLabel'

const _replaceLastElementInUrl = (url, replacement) => {
    // Remove trailing slash if it exists
    if (url.endsWith('/')) {
        url = url.slice(0, -1);
    }

    // Split the URL into parts
    var parts = url.split('/');

    // Replace the last part
    parts[parts.length - 1] = replacement;

    // Join the parts back together
    var newUrl = parts.join('/');

    // Ensure the new URL ends with a trailing slash
    if (!newUrl.endsWith('/')) {
        newUrl += '/';
    }

    return newUrl;
}

export default {
    name: "BuySection",
    components: { AmountSelector },
    props: {
        variants: {
            type: String,
            required: true,
        },
        currentVariantSku: {
            type: String,
            required: false,
        },
        productCode: {
            type: String,
            required: false,
        },
        pdf: {
            type: String,
            required: false,
        },
        soldOutText: {
            type: String,
            required: false,
        },
        soldOutReason: {
            type: String,
            required: false,
        },
        rolePageUrl: {
            type: String,
            required: false,
        },
        basketPageUrl: {
            type: String,
            required: true,
        },
        itemName: {
            type: String,
            required: false,
        },
        openRecommendationOverlay: {
            type: Boolean,
            required: false,
        },
        hasRequiredRights: {
            type: Boolean,
            required: true
        }
    },
    setup(props) {
        const store = useStore();

        const unSubmitted = ref();
        const isSubmitting = ref(false);

        const parsedVariants = props.variants ? JSON.parse(props.variants) : "";
        // Adding placeholder variant to the top of list of variants. This item is meant to be
        // read only to the user. Copied data from the first item in the to make sure it has
        // the same structure and the price can be displayed
        const placeholderVariant = props.variants
            ? {
                  ...parsedVariants.variantList[0],
                  sku: "-",
                  displayName: parsedVariants.placeholder,
              }
            : "";
        const variantData = computed(() => {
            // Extend the variant list with the placeholder variant
            var variants = {
                ...parsedVariants,
                variantList: [
                    placeholderVariant,
                    ...parsedVariants.variantList,
                ],
            };
            return variants;
        });
        const pdfData = computed(() =>
            props.pdf ? JSON.parse(props.pdf) : null
        );
        const currentVariantSku = ref(
            props.currentVariantSku ?? placeholderVariant.sku,
        );
        const currentVariant = computed(() =>
            variantData.value.variantList.find(
                (variant) => variant.sku === currentVariantSku.value,
            ),
        );
        const amount = ref(variantData.value.startAmount ?? variantData.value.minAmount);
        const currentUrlName = ref(variantData.value.productUrlName);
        const setAmount = (inputAmount) => (amount.value = inputAmount);

        const hasRequiredRights = props.hasRequiredRights;
        const displayHasRequiredRightsMessage = !hasRequiredRights ? "kb-product-detail__error-message--show" : 'kb-product-detail__error-message'

        // It should update the url when the variant changes, but not 
        // until the user has picked one from the dropdownlist
        watch(currentVariant, (variant) => {
            updatePageUrlWithVariantName(variant);
        });

        // updating url with current variant/sku so user can share direct link
        const updatePageUrlWithVariantName = function (variant) {
            const currentUrl = window.location.href;
            const newUrl = _replaceLastElementInUrl(currentUrl, variant.urlName);
            window.history.replaceState("ProductDetailPage", "", newUrl);

            currentUrlName.value = variant.urlName;
        };

        const updateDisplayNoVariantChosenMessage = function () {
            if (!unSubmitted.value) {
                displayNoVariantChosenMessage.value =
                    currentVariant.value.sku === placeholderVariant.sku
                        ? "kb-product-detail__error-message--show"
                        : "";
            }
        };

        const addToCart = async (e) => {
            // prevent submit if min/max amount is not correct
            if (amount.value < variantData.value.minAmount) return;
            if (amount.value > variantData.value.maxAmount) return;

            unSubmitted.value = false;

            updateDisplayNoVariantChosenMessage();

            if (currentVariant.value.sku === placeholderVariant.sku) {
                console.error(
                    "No variant selected, can not add to basket without"
                );
                return;
            }

            isSubmitting.value = true;
            
            try {
                await store.dispatch("basket/addToBasket", {
                    sku: currentVariant.value.sku,
                    quantity: amount.value
                })
                const atcVariant = currentVariant.value
                const atcPrice = atcVariant.price?.price ?? 0
                const atcBeforePrice = atcVariant.price?.beforePrice
                const atcDiscount = atcBeforePrice && atcBeforePrice > atcPrice
                    ? Math.round((atcBeforePrice - atcPrice) * 100) / 100
                    : 0
                const atcItem = {
                    item_id: atcVariant.sku,
                    item_name: props.itemName ?? atcVariant.displayName,
                    item_brand: 'cancer.dk',
                    price: atcPrice,
                    discount: atcDiscount,
                    quantity: amount.value,
                    index: 0
                }
                if (atcVariant.displayName) atcItem.item_variant = atcVariant.displayName
                trackAddToCart([atcItem])
                if (props.openRecommendationOverlay) {
                    store.commit('main/set_page_overlay_content_data', {
                        variantSku: currentVariant.value.sku,
                        productCode: props.productCode
                    })
                    store.dispatch('main/openPageOverlay', {
                        overlayContent: 'ProductAddedOverlay',
                        returnTarget: e.currentTarget
                    })
                }
                else {
                    window.location.href = props.basketPageUrl
                }
            }
            finally {
                isSubmitting.value = false;
            }
        };

        onMounted(() => {
            const variant = currentVariant.value
            if (!variant || variant.sku === placeholderVariant.sku) return

            const segments = window.location.pathname.split('/').filter(Boolean)
            const produkterIdx = segments.findIndex(s => s === 'produkter' || s === 'pjecer' || s === 'produktvarer')
            const categorySegments = produkterIdx >= 0
                ? segments.slice(produkterIdx, -1)
                : []

            const price = variant.price?.price ?? 0
            const beforePrice = variant.price?.beforePrice
            const discount = beforePrice && beforePrice > price
                ? Math.round((beforePrice - price) * 100) / 100
                : 0

            const viewItem = {
                item_id: variant.sku,
                item_name: props.itemName ?? variant.displayName,
                item_brand: 'cancer.dk',
                price,
                discount,
                quantity: 1,
                item_category: categorySegments[0] ?? null,
                item_category2: categorySegments[1] ?? null,
                item_category3: categorySegments[2] ?? null,
                index: 0
            }
            if (variant.displayName) viewItem.item_variant = variant.displayName
            trackViewItem(viewItem)
        })

        const openRequestRoleOverlay = () => {
            store.commit('main/set_page_overlay_content_data', {
                rolePageUrl: props.rolePageUrl
            })

            store.dispatch('main/openPageOverlay', {
                overlayContent: 'FrivilligeShopWrongRoleModal'
            })
        }

        const displayNoVariantChosenMessage = ref();
        const displayMaxAmountMessage = ref();
        const displayMinAmountMessage = ref();

        const displayMessage = function (show) {
            if (!unSubmitted.value) {
                if (show === "max-error") {
                    displayMinAmountMessage.value = "";
                    displayMaxAmountMessage.value = show
                        ? "kb-product-detail__error-message--show"
                        : "";
                } else if (show === "min-error") {
                    displayMaxAmountMessage.value = "";
                    displayMinAmountMessage.value = show
                        ? "kb-product-detail__error-message--show"
                        : "";
                } else {
                    displayMinAmountMessage.value = "";
                    displayMaxAmountMessage.value = "";
                }
            }
        };
        return {
            amount,
            currentVariant,
            currentVariantSku,
            displayMaxAmountMessage,
            displayMinAmountMessage,
            displayNoVariantChosenMessage,
            pdfData,
            unSubmitted,
            isSubmitting,
            variantData,
            hasRequiredRights,
            displayHasRequiredRightsMessage,
            addToCart,
            openRequestRoleOverlay,
            displayMessage,
            getLabel: (labelKey, params, allowEmpty = false) => GetLabel({ labelKey, labelCollection: 'buySection', params, allowEmpty}),
            setAmount,
            updateDisplayNoVariantChosenMessage,
        };
    },
};
